import React, {
    ReactElement, useEffect, useState,
} from "react";
import { Form } from "react-bootstrap";
import { Loading } from "..";
import useRouting, { Link, Paths, useParams } from "../../contexts/Routing";
import {
    useGetOrganizationsQuery, useSaveOrganizationMutation,
} from "../../__generated__/api";
import { ErrorRemote } from "../../pages/error/Error";
import { UserList } from "./Users";

export default function OrganizationDetail(): ReactElement {
    const { id } = useParams(Paths.ORGANIZATIONS_ID);
    const isNew = id === "new";
    const { historyPush } = useRouting();

    const [saveOrganization] = useSaveOrganizationMutation();
    const [organizationName, setOrganizationName] = useState<string>("");
    const [organizationDomain, setOrganizationDomain] = useState<string>("");

    const { data, error, loading: isLoading } = useGetOrganizationsQuery({
        variables: {
            companyIds: [id],
            limit: 1,
        },
        skip: isNew,
    });

    useEffect(() => {
        if (data?.companies?.results?.isNotEmpty) {
            setOrganizationDomain(data.companies.results[0].domain);
            setOrganizationName(data.companies.results[0].name);
        }
    }, [data?.companies?.results[0]]);

    if (isLoading) {
        return <Loading />;
    }

    if (error) {
        return <ErrorRemote error={error} />;
    }

    const handleChangeOrganization = async (organizationId?: string): Promise<void> => {
        await saveOrganization({
            update(cache) {
                cache.evict({ id: "ROOT_QUERY", fieldName: "companies" });
            },
            variables: {
                id: organizationId,
                organization: {
                    name: organizationName,
                    domainName: organizationDomain,
                },
            },
        });

        historyPush(Paths.CONTACTS_ENTITY, { entity: "organizations" });
    };

    return (
        <>
            <div className="border p-3 rounded bg-white mb-4">
                <h5 className="text-light mb-3">
                    {isNew ? "Add an organization" : "Edit organization"}
                </h5>
                <div className="d-flex">
                    <form
                        className="flex-grow-1"
                        onSubmit={event => {
                            event.preventDefault();
                            handleChangeOrganization(isNew ? undefined : id);
                        }}
                    >
                        <div className="d-flex row align-items-center mb-2 w-100">
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder" htmlFor="addOrganizationName">Name</Form.Label>
                                <Form.Control
                                    id="addOrganizationName"
                                    className="form-control w-100 col"
                                    type="text"
                                    onChange={element => setOrganizationName(element.target.value)}
                                    value={organizationName}
                                    required
                                    autoFocus
                                />
                                <Form.Text>
                                    The organization name
                                </Form.Text>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bolder" htmlFor="addOrganizationDomainName">
                                    Domain Name
                                </Form.Label>
                                <Form.Control
                                    id="addOrganizationDomainName"
                                    type="text"
                                    className="form-control w-100"
                                    onChange={element => setOrganizationDomain(element.target.value)}
                                    value={organizationDomain}
                                    required
                                />
                                <Form.Text>
                                    The domain the of the organization
                                </Form.Text>
                            </Form.Group>
                        </div>
                        <div className="d-flex justify-content-end mt-3 fixed-bottom border-top p-3 bg-white">
                            <button
                                type="submit"
                                className="btn btn-primary"
                            >
                                {isNew ? "Add" : "Save"}
                            </button>
                        </div>
                    </form>

                    <div className="text-light col-6 col-lg-4 col-xl-3 border-start px-4 ms-2">
                        {!isNew ? (
                            <p>
                                Here you can edit the name and domain name of the organization.
                                The domain name helps us identify the organization and automatically group users by email.
                            </p>
                        ) : (
                            <p className="mt-4">
                                To create a new organization enter the name of the organization and the primary domain name.
                                The domain name helps us identify the organization and automatically group users by email.
                            </p>
                        )}

                    </div>

                </div>

            </div>

            {id && !isNew && <UserList companyId={id} />}
        </>
    );
}
